import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import {
  ChevronDown,
  ChevronRight,
  Plus,
  Trash2,
  GripVertical,
  PackageOpen,
  PackageCheck,
  Circle,
  Clock,
  CheckCircle,
  ShieldCheck,
} from "lucide-react";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Table, TableBody } from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from "@/components/ui/hover-card";
import { EquipmentAssignmentRow } from "./EquipmentAssignmentRow";
import { EquipmentAssignmentEditDialog } from "./EquipmentAssignmentEditDialog";
import { useIsMobile } from "@/hooks/use-mobile";
import type { CameraWithPlan } from "@/hooks/useCameraPlans";
import { getCameraIcon } from "@/components/project-detail/camera-plans/CameraIconPicker";
import type { CameraIcon } from "@/types/camera-plans";
import type {
  ProjectEquipmentGroupWithAssignments,
  ProjectEquipmentAssignment,
  PackingState,
  RiggingState,
} from "@/hooks/useProjectEquipment";
import type { ConflictSummary } from "@/hooks/useEquipmentConflicts";
import type { EquipmentInstance } from "@/hooks/useEquipmentInstances";
import { formatEquipmentName } from "@/lib/equipment-utils";

type StateView = "packing" | "rigging";

const PACKING_STATES: {
  value: PackingState;
  label: string;
  icon: React.ElementType;
  className: string;
}[] = [
  {
    value: "open",
    label: "Open",
    icon: PackageOpen,
    className: "text-muted-foreground",
  },
  {
    value: "packed",
    label: "Packed",
    icon: PackageCheck,
    className: "text-emerald-600",
  },
];

const RIGGING_STATES: {
  value: RiggingState;
  label: string;
  icon: React.ElementType;
  className: string;
}[] = [
  {
    value: "open",
    label: "Open",
    icon: Circle,
    className: "text-muted-foreground",
  },
  {
    value: "in_progress",
    label: "In Progress",
    icon: Clock,
    className: "text-amber-500",
  },
  {
    value: "ready",
    label: "Ready",
    icon: CheckCircle,
    className: "text-emerald-600",
  },
  {
    value: "tested",
    label: "Tested",
    icon: ShieldCheck,
    className: "text-blue-600",
  },
];

interface EquipmentGroupCardProps {
  group: ProjectEquipmentGroupWithAssignments;
  stateView: StateView;
  conflicts?: Record<string, ConflictSummary>;
  instancesByEquipmentId?: Record<string, EquipmentInstance[]>;
  currentProjectId: string;
  newAssignmentId?: string | null;
  onNewAssignmentHandled?: (assignmentId: string) => void;
  onUpdateGroup: (
    updates: Partial<ProjectEquipmentGroupWithAssignments>,
  ) => void;
  onDeleteGroup: () => void;
  onAddEquipment: (groupId: string) => void;
  onUpdateAssignment: (
    assignmentId: string,
    updates: {
      amount?: number;
      notes?: string;
      equipment_id?: string | null;
      temporary_equipment_id?: string | null;
    },
  ) => void;
  onDeleteAssignment: (assignmentId: string) => void;
  onCreateTemporary: (name: string) => Promise<string>;
  onUpdateTemporary: (id: string, name: string) => void;
  onAssignInstance: (assignmentId: string, instanceId: string) => void;
  onUnassignInstance: (instanceAssignmentId: string) => void;
  cameras?: CameraWithPlan[];
}

function CameraBadgePopoverContent({
  cam,
  projectId,
}: {
  cam: CameraWithPlan;
  projectId: string;
}) {
  return (
    <PopoverContent className="w-auto max-w-xs p-3">
      <CameraBadgeContent cam={cam} projectId={projectId} />
    </PopoverContent>
  );
}

function CameraBadgeContent({
  cam,
  projectId,
}: {
  cam: CameraWithPlan;
  projectId: string;
}) {
  const Icon = getCameraIcon(cam.icon as CameraIcon);
  const operators =
    cam.cameraFunctionAssignments
      ?.map((assignment) => assignment.functionAssignment?.person)
      .filter(Boolean) ?? [];

  return (
    <>
      <div className="flex items-center gap-2 mb-1">
        <Icon className="h-4 w-4 text-muted-foreground" />
        <span className="font-medium text-sm">Cam{cam.number}</span>
      </div>
      {cam.description && (
        <p className="text-sm text-muted-foreground mb-1">{cam.description}</p>
      )}
      {operators.length > 0 && (
        <p className="text-sm text-muted-foreground mb-1">
          {operators.map((p) => `${p!.firstName} ${p!.lastName}`).join(", ")}
        </p>
      )}
      <Link
        to={`/projects/${projectId}/plan/camera-plans/${cam.cameraPlanID}`}
        className="text-sm text-primary hover:underline"
      >
        Open in camera plan
      </Link>
    </>
  );
}

export function EquipmentGroupCard({
  group,
  stateView,
  conflicts,
  instancesByEquipmentId,
  currentProjectId,
  newAssignmentId,
  onNewAssignmentHandled,
  onUpdateGroup,
  onDeleteGroup,
  onAddEquipment,
  onUpdateAssignment,
  onDeleteAssignment,
  onCreateTemporary,
  onUpdateTemporary,
  onAssignInstance,
  onUnassignInstance,
  cameras,
}: EquipmentGroupCardProps) {
  const isMobile = useIsMobile();
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: group.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : undefined,
  };

  const isCollapsed = group.is_collapsed || false;
  const [isEditingName, setIsEditingName] = useState(false);
  const [name, setName] = useState(group.name);
  const [editingAssignment, setEditingAssignment] =
    useState<ProjectEquipmentAssignment | null>(null);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);

  useEffect(() => {
    if (!isEditingName) {
      setName(group.name);
    }
  }, [group.name, isEditingName]);

  useEffect(() => {
    if (!isMobile || !newAssignmentId) return;
    const newAssignment = group.assignments.find(
      (a) => a.id === newAssignmentId,
    );
    if (!newAssignment) return;
    setEditingAssignment(newAssignment);
    onNewAssignmentHandled?.(newAssignment.id);
  }, [group.assignments, isMobile, newAssignmentId, onNewAssignmentHandled]);

  useEffect(() => {
    if (!editingAssignment) return;
    const updatedAssignment = group.assignments.find(
      (assignment) => assignment.id === editingAssignment.id,
    );
    if (updatedAssignment && updatedAssignment !== editingAssignment) {
      setEditingAssignment(updatedAssignment);
    }
  }, [editingAssignment, group.assignments]);

  const handleNameBlur = () => {
    setIsEditingName(false);
    if (name !== group.name && name.trim()) {
      onUpdateGroup({ name: name.trim() });
    } else {
      setName(group.name);
    }
  };

  const handleNameKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      handleNameBlur();
    }
    if (e.key === "Escape") {
      setName(group.name);
      setIsEditingName(false);
    }
  };

  const handleCollapseChange = (collapsed: boolean) => {
    onUpdateGroup({ is_collapsed: collapsed });
  };

  const currentState =
    stateView === "packing" ? group.packing_state : group.rigging_state;
  const stateOptions =
    stateView === "packing" ? PACKING_STATES : RIGGING_STATES;
  const currentStateOption = stateOptions.find((o) => o.value === currentState);

  const equipmentSummary = group.assignments
    .map((a) => {
      const name =
        a.equipment?.short_name ||
        (a.equipment
          ? formatEquipmentName(a.equipment.name, a.equipment.manufacturer)
          : null) ||
        a.temporary_equipment?.name ||
        "Unnamed";
      const prefix = a.amount > 1 ? `${a.amount}x ` : "";
      const instanceIds =
        a.instance_assignments
          ?.map((ia) => ia.instance?.identifier)
          .filter(Boolean) || [];
      const instanceSuffix =
        instanceIds.length > 0 ? ` (${instanceIds.join(", ")})` : "";
      return `${prefix}${name}${instanceSuffix}`;
    })
    .join(", ");

  const handleStateChange = (value: string) => {
    if (stateView === "packing") {
      onUpdateGroup({ packing_state: value as PackingState });
    } else {
      onUpdateGroup({ rigging_state: value as RiggingState });
    }
  };

  return (
    <>
      <Card ref={setNodeRef} style={style} {...attributes}>
        <Collapsible
          open={!isCollapsed}
          onOpenChange={(open) => handleCollapseChange(!open)}
        >
          <CardHeader className="py-3">
            {isMobile ? (
              <div className="space-y-1">
                <div className="flex items-center gap-2">
                  <GripVertical
                    className="h-4 w-4 flex-shrink-0 text-muted-foreground cursor-grab touch-none"
                    {...listeners}
                  />
                  <CollapsibleTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-6 w-6 flex-shrink-0"
                    >
                      {isCollapsed ? (
                        <ChevronRight className="h-4 w-4" />
                      ) : (
                        <ChevronDown className="h-4 w-4" />
                      )}
                    </Button>
                  </CollapsibleTrigger>
                  <span className="font-medium">{group.name}</span>
                  <div className="ml-auto flex items-center gap-2">
                    <Select
                      value={currentState}
                      onValueChange={handleStateChange}
                    >
                      <SelectTrigger className="h-8 w-auto min-w-0">
                        {currentStateOption &&
                          (() => {
                            const StateIcon = currentStateOption.icon;
                            return (
                              <StateIcon
                                className={`h-4 w-4 ${currentStateOption.className}`}
                              />
                            );
                          })()}
                      </SelectTrigger>
                      <SelectContent>
                        {stateOptions.map((option) => {
                          const OptionIcon = option.icon;
                          return (
                            <SelectItem key={option.value} value={option.value}>
                              <span className="flex items-center gap-2">
                                <OptionIcon
                                  className={`h-3.5 w-3.5 ${option.className}`}
                                />
                                {option.label}
                              </span>
                            </SelectItem>
                          );
                        })}
                      </SelectContent>
                    </Select>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 text-destructive hover:text-destructive"
                      onClick={() => setIsDeleteDialogOpen(true)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
                {equipmentSummary && (
                  <p className="text-sm text-muted-foreground pl-8 line-clamp-2">
                    {equipmentSummary}
                  </p>
                )}
                {cameras && cameras.length > 0 && (
                  <div className="flex items-center gap-1 pl-8 flex-wrap">
                    {cameras.map((cam) => {
                      const MobileIcon = getCameraIcon(cam.icon as CameraIcon);
                      return (
                        <Popover key={cam.id}>
                          <PopoverTrigger asChild>
                            <button
                              className="inline-flex items-center gap-1 text-xs px-1.5 py-0.5 rounded bg-muted hover:bg-muted/80 text-foreground"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <MobileIcon className="h-3 w-3" />
                              Cam{cam.number}
                            </button>
                          </PopoverTrigger>
                          <CameraBadgePopoverContent
                            cam={cam}
                            projectId={currentProjectId}
                          />
                        </Popover>
                      );
                    })}
                  </div>
                )}
              </div>
            ) : (
              <div className="flex items-center gap-3">
                <GripVertical
                  className="h-4 w-4 text-muted-foreground cursor-grab touch-none"
                  {...listeners}
                />
                <CollapsibleTrigger asChild>
                  <Button variant="ghost" size="icon" className="h-6 w-6">
                    {isCollapsed ? (
                      <ChevronRight className="h-4 w-4" />
                    ) : (
                      <ChevronDown className="h-4 w-4" />
                    )}
                  </Button>
                </CollapsibleTrigger>

                {isEditingName ? (
                  <Input
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    onBlur={handleNameBlur}
                    onKeyDown={handleNameKeyDown}
                    className="h-8 w-48"
                    autoFocus
                  />
                ) : (
                  <button
                    className="font-medium hover:underline text-left"
                    onClick={() => setIsEditingName(true)}
                  >
                    {group.name}
                  </button>
                )}

                {equipmentSummary && (
                  <span
                    className="text-sm text-muted-foreground truncate max-w-md"
                    title={equipmentSummary}
                  >
                    {equipmentSummary}
                  </span>
                )}

                {cameras && cameras.length > 0 && (
                  <div className="flex items-center gap-1 flex-wrap">
                    {cameras.map((cam) => {
                      const Icon = getCameraIcon(cam.icon as CameraIcon);
                      return (
                        <HoverCard key={cam.id} openDelay={0} closeDelay={100}>
                          <HoverCardTrigger asChild>
                            <button
                              className="inline-flex items-center gap-1 text-xs px-1.5 py-0.5 rounded bg-muted hover:bg-muted/80 text-foreground"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <Icon className="h-3 w-3" />
                              Cam{cam.number}
                            </button>
                          </HoverCardTrigger>
                          <HoverCardContent
                            align="start"
                            className="w-auto max-w-xs p-3"
                          >
                            <CameraBadgeContent
                              cam={cam}
                              projectId={currentProjectId}
                            />
                          </HoverCardContent>
                        </HoverCard>
                      );
                    })}
                  </div>
                )}

                <div className="ml-auto flex items-center gap-2">
                  <Select
                    value={currentState}
                    onValueChange={handleStateChange}
                  >
                    <SelectTrigger className="h-8 w-32">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {stateOptions.map((option) => {
                        const OptionIcon = option.icon;
                        return (
                          <SelectItem key={option.value} value={option.value}>
                            <span className="flex items-center gap-2">
                              <OptionIcon
                                className={`h-3.5 w-3.5 ${option.className}`}
                              />
                              {option.label}
                            </span>
                          </SelectItem>
                        );
                      })}
                    </SelectContent>
                  </Select>

                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 text-destructive hover:text-destructive"
                    onClick={() => setIsDeleteDialogOpen(true)}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            )}
          </CardHeader>

          <CollapsibleContent>
            <CardContent className="pt-0">
              {isMobile && cameras && cameras.length > 0 && (
                <div className="mb-2 space-y-2 border-b pb-3">
                  {cameras.map((cam) => (
                    <div key={cam.id} className="rounded-md bg-muted/40 p-2">
                      <CameraBadgeContent
                        cam={cam}
                        projectId={currentProjectId}
                      />
                    </div>
                  ))}
                </div>
              )}

              {group.assignments.length > 0 &&
                (isMobile ? (
                  <div className="space-y-2">
                    {group.assignments.map((assignment) => (
                      <EquipmentAssignmentRow
                        key={assignment.id}
                        assignment={assignment}
                        isNew={assignment.id === newAssignmentId}
                        conflict={
                          assignment.equipment_id
                            ? conflicts?.[assignment.equipment_id]
                            : undefined
                        }
                        instances={
                          assignment.equipment_id
                            ? instancesByEquipmentId?.[assignment.equipment_id]
                            : undefined
                        }
                        currentProjectId={currentProjectId}
                        onNewHandled={() =>
                          onNewAssignmentHandled?.(assignment.id)
                        }
                        onUpdate={(updates) =>
                          onUpdateAssignment(assignment.id, updates)
                        }
                        onEdit={() => setEditingAssignment(assignment)}
                        onDelete={() => onDeleteAssignment(assignment.id)}
                        onCreateTemporary={onCreateTemporary}
                        onUpdateTemporary={onUpdateTemporary}
                        onAssignInstance={(instanceId) =>
                          onAssignInstance(assignment.id, instanceId)
                        }
                        onUnassignInstance={onUnassignInstance}
                      />
                    ))}
                  </div>
                ) : (
                  <Table className="table-fixed">
                    <TableBody>
                      {group.assignments.map((assignment) => (
                        <EquipmentAssignmentRow
                          key={assignment.id}
                          assignment={assignment}
                          isNew={assignment.id === newAssignmentId}
                          conflict={
                            assignment.equipment_id
                              ? conflicts?.[assignment.equipment_id]
                              : undefined
                          }
                          instances={
                            assignment.equipment_id
                              ? instancesByEquipmentId?.[
                                  assignment.equipment_id
                                ]
                              : undefined
                          }
                          currentProjectId={currentProjectId}
                          onNewHandled={() =>
                            onNewAssignmentHandled?.(assignment.id)
                          }
                          onUpdate={(updates) =>
                            onUpdateAssignment(assignment.id, updates)
                          }
                          onEdit={() => setEditingAssignment(assignment)}
                          onDelete={() => onDeleteAssignment(assignment.id)}
                          onCreateTemporary={onCreateTemporary}
                          onUpdateTemporary={onUpdateTemporary}
                          onAssignInstance={(instanceId) =>
                            onAssignInstance(assignment.id, instanceId)
                          }
                          onUnassignInstance={onUnassignInstance}
                        />
                      ))}
                    </TableBody>
                  </Table>
                ))}

              <Button
                offlineEdit
                variant="link"
                size="sm"
                className="mt-2 h-auto p-0 text-muted-foreground hover:text-foreground"
                onClick={() => onAddEquipment(group.id)}
              >
                <Plus className="h-4 w-4 mr-1" />
                Add equipment
              </Button>
            </CardContent>
          </CollapsibleContent>
        </Collapsible>
      </Card>

      {editingAssignment && (
        <EquipmentAssignmentEditDialog
          open={!!editingAssignment}
          onOpenChange={(open) => !open && setEditingAssignment(null)}
          assignment={editingAssignment}
          instances={
            editingAssignment.equipment_id
              ? instancesByEquipmentId?.[editingAssignment.equipment_id]
              : undefined
          }
          onSave={(updates) =>
            onUpdateAssignment(editingAssignment.id, updates)
          }
          onCreateTemporary={onCreateTemporary}
          onUpdateTemporary={onUpdateTemporary}
          onDelete={() => onDeleteAssignment(editingAssignment.id)}
          onAssignInstance={(instanceId) =>
            onAssignInstance(editingAssignment.id, instanceId)
          }
          onUnassignInstance={onUnassignInstance}
        />
      )}

      <AlertDialog
        open={isDeleteDialogOpen}
        onOpenChange={setIsDeleteDialogOpen}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete equipment group?</AlertDialogTitle>
            <AlertDialogDescription>
              This will delete the group "{group.name}" and all its equipment
              assignments. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={onDeleteGroup}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
